import React, { useState, useRef, useEffect } from 'react';
import { DataCategory, ValidationResponse, SAPSubCategory, SAPMainCategory } from '../../types';
import { api } from '../../services/api';
import { useSAPCategories } from '../../hooks/useSAPCategories';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import ValidationResults from '../display/ValidationResults';
import FieldInstructions from '../display/FieldInstructions';
import BusinessRules from '../display/BusinessRules';
import AddFieldInstructionForm from '../forms/AddFieldInstructionForm';
import ExcelUploadForm from '../forms/ExcelUploadForm';
import AIHelper from '../ui/AIHelper';
import LoadingAnimation from '../ui/LoadingAnimation';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Upload, Download, FileText, Filter, Plus, Loader2, CheckCircle2, AlertTriangle, XCircle, BarChart3, X, FileDown } from 'lucide-react';
import * as XLSX from 'xlsx';

interface MainContentProps {
  category: DataCategory;
  onNavigate?: (target: string) => void;
}

const MainContent: React.FC<MainContentProps> = ({ category, onNavigate }) => {
  const [activeTab, setActiveTab] = useState<'upload' | 'instructions' | 'data'>('upload');
  const [validationResults, setValidationResults] = useState<ValidationResponse | null>(null);
  const [fileData, setFileData] = useState<any[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [excelHeaders, setExcelHeaders] = useState<string[]>([]);
  const [selectedDataCategory, setSelectedDataCategory] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<SAPSubCategory | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [showExcelUpload, setShowExcelUpload] = useState<boolean>(false);
  const [refreshInstructions, setRefreshInstructions] = useState<number>(0);
  const [subcategories, setSubcategories] = useState<SAPSubCategory[]>([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [showDropdownHighlight, setShowDropdownHighlight] = useState(false);
  const [hasInteractedWithDropdown, setHasInteractedWithDropdown] = useState(false);
  const [isClearingOutput, setIsClearingOutput] = useState(false);
  const [mainCategories, setMainCategories] = useState<SAPMainCategory[]>([]);
  const [loadingMainCategories, setLoadingMainCategories] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState({ current: 0, total: 0, percentage: 0 });
  const [checkedFieldsForValidation, setCheckedFieldsForValidation] = useState<string[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCheckedFieldsForValidation(null);
  }, [selectedDataCategory]);

  const { categories, downloadFile } = useSAPCategories();
  const { hasPermission } = useAuth();
  const toast = useToast();

  // Load main categories on component mount
  useEffect(() => {
    const loadMainCategories = async () => {
      setLoadingMainCategories(true);
      try {
        console.log('Loading main categories...');
        const response = await api.getMainCategories();
        console.log('Main categories response:', response);
        if (response.success) {
          setMainCategories(response.data || []);
          console.log('Set main categories:', response.data?.length || 0);
        } else {
          console.error('Failed to load main categories:', response);
        }
      } catch (error) {
        console.error('Error loading main categories:', error);
        // Retry once after a short delay
        setTimeout(async () => {
          try {
            console.log('Retrying main categories load...');
            const retryResponse = await api.getMainCategories();
            if (retryResponse.success) {
              setMainCategories(retryResponse.data || []);
              console.log('Retry successful, set main categories:', retryResponse.data?.length || 0);
            }
          } catch (retryError) {
            console.error('Retry failed:', retryError);
          }
        }, 1000);
      } finally {
        setLoadingMainCategories(false);
      }
    };

    loadMainCategories();
  }, []);

  // Load subcategories when category changes
  useEffect(() => {
    const loadSubcategories = async () => {
      if (!category) {
        setSubcategories([]);
        return;
      }
      
      setLoadingSubcategories(true);
      console.log('Loading subcategories for category:', category);
      console.log('Available categories:', categories?.length || 0);
      console.log('Available mainCategories:', mainCategories?.length || 0);
      
      try {
        // First try to find in the already loaded categories from useSAPCategories hook
        let mainCategory = categories?.find(cat => 
          cat.MainCategoryName.replace(/\s+/g, '') === category
        );
        
        // If not found, try to find in mainCategories
        if (!mainCategory) {
          mainCategory = mainCategories?.find(cat => 
            cat.MainCategoryName.replace(/\s+/g, '') === category
          );
        }
        
        console.log('Found mainCategory:', mainCategory?.MainCategoryName);
        
        if (mainCategory && mainCategory.SubCategories && mainCategory.SubCategories.length > 0) {
          console.log('Setting subcategories from mainCategory:', mainCategory.SubCategories.length);
          setSubcategories(mainCategory.SubCategories);
        } else {
          // If still not found, try to fetch subcategories directly
          console.log('Fetching subcategories directly for:', category);
          try {
            const response = await api.getSubcategoriesByMainCategoryName(category);
            if (response.success) {
              console.log('Direct fetch successful:', response.data?.length || 0, 'subcategories');
              setSubcategories(response.data || []);
            } else {
              console.log('Direct fetch failed:', response);
              // Final fallback: try to get from the original categories API
              try {
                console.log('Trying fallback to original categories API...');
                const fallbackResponse = await api.getSAPCategories();
                if (fallbackResponse.success && fallbackResponse.data) {
                  const fallbackCategory = fallbackResponse.data.find(cat => 
                    cat.MainCategoryName.replace(/\s+/g, '') === category
                  );
                  if (fallbackCategory && fallbackCategory.SubCategories) {
                    console.log('Fallback successful:', fallbackCategory.SubCategories.length, 'subcategories');
                    setSubcategories(fallbackCategory.SubCategories);
                  } else {
                    setSubcategories([]);
                  }
                } else {
                  setSubcategories([]);
                }
              } catch (fallbackError) {
                console.error('Fallback also failed:', fallbackError);
                setSubcategories([]);
              }
            }
          } catch (fetchError) {
            console.error('Error fetching subcategories directly:', fetchError);
            setSubcategories([]);
          }
        }
      } catch (error) {
        console.error('Error loading subcategories:', error);
        setSubcategories([]);
      } finally {
        setLoadingSubcategories(false);
      }
    };

    loadSubcategories();
  }, [category, categories, mainCategories]);

  // Trigger dropdown highlight on page load
  useEffect(() => {
    if (subcategories && subcategories.length > 0 && !hasInteractedWithDropdown) {
      setShowDropdownHighlight(true);
      
      // Don't auto-select any subcategory - let user choose
      // Keep selectedDataCategory empty to show "Select Category" as default
    }
  }, [subcategories, hasInteractedWithDropdown, selectedDataCategory]);

  // Switch to upload tab if instructions tab is active but no category is selected
  useEffect(() => {
    if (activeTab === 'instructions' && !selectedDataCategory) {
      setActiveTab('upload');
    }
  }, [activeTab, selectedDataCategory]);

  // Reset selectedDataCategory when main category changes
  useEffect(() => {
    setSelectedDataCategory('');
    setSelectedSubCategory(null);
    setActiveTab('upload');
    setValidationResults(null);
    setFileData([]);
    setUploadedFile(null);
    setHasInteractedWithDropdown(false);
    setShowDropdownHighlight(false);
  }, [category]);

  const getCategoryTitle = (category: DataCategory) => {
    // If main categories are still loading, show a loading state
    if (loadingMainCategories) {
      return 'Loading...';
    }

    // Find the main category that matches the current category
    // The category prop should match the MainCategoryName (with spaces removed)
    const mainCategory = mainCategories?.find(cat => 
      cat.MainCategoryName.replace(/\s+/g, '') === category
    );

    if (mainCategory) {
      return mainCategory.MainCategoryName;
    }

    // If not found in API data, return the category as is (it might be a new category)
    return category.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
  };


  // Get dynamic tab name based on selected subcategory
  const getTabName = () => {
    if (selectedDataCategory) {
      return selectedDataCategory;
    }
    return 'Customer Data'; // Default fallback
  };

  // Handle subcategory selection
  const handleSubCategoryChange = (subCategoryName: string) => {
    const subCategory = subcategories?.find(sub => sub.SubCategoryName === subCategoryName);
    setSelectedSubCategory(subCategory || null);
    setSelectedDataCategory(subCategoryName);
    
    // Clear output when user selects a different category
    if (selectedDataCategory && selectedDataCategory !== subCategoryName) {
      setIsClearingOutput(true);
      // Add a small delay for smooth transition
      setTimeout(() => {
        setValidationResults(null);
        setFileData([]);
        setUploadedFile(null);
        setExcelHeaders([]);
        setIsClearingOutput(false);
      }, 300);
    }
    
    // Remove highlight when user interacts with dropdown
    if (!hasInteractedWithDropdown) {
      setHasInteractedWithDropdown(true);
      setShowDropdownHighlight(false);
    }
  };

  const handleFileUpload = async (data: any[], file: File, headers: string[] = []) => {
    const subcategoryName = selectedSubCategory?.SubCategoryName || selectedDataCategory || undefined;
    if (!subcategoryName) {
      toast.warning('No category selected', 'Please select a subcategory from "Choose Data Category" first, then upload and validate.');
      return;
    }

    setFileData(data);
    setUploadedFile(file);
    setExcelHeaders(headers);
    setIsValidating(true);
    setValidationProgress({ current: 0, total: 0, percentage: 0 });

    try {
      // Split into batches — small enough to finish within Edge Function timeout (60s)
      const BATCH_SIZE = 25;
      const CONCURRENCY = 2; // 2 batches in parallel — stays within Supabase rate limits

      const batches: { start: number; batch: any[] }[] = [];
      for (let i = 0; i < data.length; i += BATCH_SIZE) {
        batches.push({ start: i, batch: data.slice(i, i + BATCH_SIZE) });
      }
      console.log(`Validating ${data.length} rows in ${batches.length} batches (${CONCURRENCY} concurrent)`);
      setValidationProgress({ current: 0, total: data.length, percentage: 0 });

      let completedRows = 0;
      const allBatchResults: { start: number; results: any[] }[] = [];

      // Process a single batch with retry
      const processBatch = async (start: number, batch: any[]): Promise<{ start: number; results: any[] }> => {
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            if (attempt > 0) {
              console.warn(`Batch at row ${start} retry ${attempt + 1}/3...`);
              await new Promise(r => setTimeout(r, 2000 * attempt));
            }
            const result = await api.submitValidation(category, batch, headers, subcategoryName);
            return { start, results: result.results || [] };
          } catch (err: any) {
            if (err?.status === 402 || err?.status === 401 || err?.message?.includes('API key')) throw err;
            if (attempt >= 2) throw err;
          }
        }
        throw new Error('Batch failed after retries');
      };

      // Process in concurrent groups
      for (let i = 0; i < batches.length; i += CONCURRENCY) {
        const group = batches.slice(i, i + CONCURRENCY);
        const groupResults = await Promise.all(
          group.map(({ start, batch }) => processBatch(start, batch))
        );
        allBatchResults.push(...groupResults);
        completedRows += group.reduce((sum, g) => sum + g.batch.length, 0);
        setValidationProgress({
          current: completedRows,
          total: data.length,
          percentage: Math.round((completedRows / data.length) * 100),
        });
      }

      // Merge AI error entries into per-row ValidationResult objects with original Excel data
      // AI returns: [{ row: 1-based, field, value, error, suggestion }] — only error rows
      // We need: [{ rowNumber, status, fieldsWithIssues, message, rowData }] — ALL rows

      // Group AI errors by global row number
      // Skip "system" errors — these are not data issues, they're AI/edge-function failures
      // and should never be attached to a real data row.
      const errorsByRow = new Map<number, Array<{ field: string; error: string; suggestion: string }>>();
      let systemErrorCount = 0;
      for (const { start, results } of allBatchResults) {
        for (const r of results) {
          if (r.field === 'system' || r.row === 0) {
            systemErrorCount++;
            console.warn('Skipping system error from AI:', r);
            continue;
          }
          const globalRow = (r.row ?? 0) + start; // r.row is 1-based within batch
          if (!errorsByRow.has(globalRow)) errorsByRow.set(globalRow, []);
          errorsByRow.get(globalRow)!.push({
            field: r.field || '',
            error: r.error || '',
            suggestion: r.suggestion || '',
          });
        }
      }
      if (systemErrorCount > 0) {
        toast.warning(
          'Some batches had AI parsing issues',
          `${systemErrorCount} batch result(s) couldn\'t be parsed. Retrying these or uploading a smaller file may help.`
        );
      }

      // Build one ValidationResult per Excel row, attaching original rowData
      const allResults: any[] = data.map((rowData, idx) => {
        const rowNum = idx + 1; // 1-based
        const errors = errorsByRow.get(rowNum) || [];
        const hasErrors = errors.length > 0;

        return {
          rowNumber: rowNum,
          status: hasErrors ? 'Error' : 'Valid',
          fieldsWithIssues: errors.map(e => e.field),
          message: hasErrors
            ? errors.map(e => `${e.field}: ${e.error}${e.suggestion ? ` → ${e.suggestion}` : ''}`).join('; ')
            : 'All fields valid',
          rowData,
        };
      });

      const totalErrors = allResults.filter(r => r.status === 'Error').length;
      const finalResult: ValidationResponse = {
        success: true,
        category: category,
        results: allResults,
        summary: { total: data.length, valid: data.length - totalErrors, warnings: 0, errors: totalErrors },
        headers: headers
      };
      setValidationResults(finalResult);
      toast.success('Validation complete', `${data.length} row${data.length !== 1 ? 's' : ''} processed successfully.`);
    } catch (error: any) {
      console.error('Validation failed:', error);
      if (error?.code === 'INSUFFICIENT_TOKENS' || error?.status === 402) {
        window.dispatchEvent(new Event('insufficient-tokens'));
      } else {
        const errorMessage = error?.message || 'Validation failed. Please check your data and try again.';
        toast.error('Validation failed', errorMessage);
      }
    } finally {
      setIsValidating(false);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('File input changed');
    const file = event.target.files?.[0];
    console.log('Selected file:', file);
    if (file) {
      console.log('Processing file:', file.name, file.type, file.size);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          console.log('File read successfully');
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array', cellDates: true });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          // Extract headers first using header: 1 option
          const jsonDataWithHeaders = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          const headers = (jsonDataWithHeaders[0] as any[]) || [];
          const filteredHeaders = headers.filter(header => header && header.toString().trim() !== '');

          // Get data as objects with raw values (dates come as JS Date objects)
          const jsonDataRaw = XLSX.utils.sheet_to_json(worksheet);
          // Convert Date objects to YYYY-MM-DD HH:MM format so they match validation rules
          const jsonData = jsonDataRaw.map((row: any) => {
            const formatted: any = {};
            for (const [key, val] of Object.entries(row)) {
              if (val instanceof Date) {
                const y = val.getFullYear();
                const mo = String(val.getMonth() + 1).padStart(2, '0');
                const d = String(val.getDate()).padStart(2, '0');
                const h = String(val.getHours()).padStart(2, '0');
                const mi = String(val.getMinutes()).padStart(2, '0');
                formatted[key] = `${y}-${mo}-${d} ${h}:${mi}`;
              } else {
                formatted[key] = val;
              }
            }
            return formatted;
          });
          console.log('Parsed data:', jsonData);
          console.log('Extracted headers:', filteredHeaders);
          
          setExcelHeaders(filteredHeaders);
          handleFileUpload(jsonData, file, filteredHeaders);
        } catch (error) {
          console.error('Error reading file:', error);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const handleUploadClick = () => {
    if (!selectedDataCategory) return;
    if (validationResults) {
      setValidationResults(null);
      setFileData([]);
      setUploadedFile(null);
      setExcelHeaders([]);
    }
    const input = fileInputRef.current;
    if (input) {
      input.value = '';
      setTimeout(() => input.click(), 0);
    }
  };


  const handleDownloadTemplate = async () => {
    if (!selectedSubCategory) {
      toast.warning('No category selected', 'Please select a subcategory first.');
      return;
    }
    try {
      await downloadFile(selectedSubCategory.SubCategoryID, 'template');
      toast.success('Download started', 'Template file is downloading.');
    } catch (error) {
      console.error('Failed to download template:', error);
      toast.error('Download failed', 'Failed to download template file.');
    }
  };

  const handleDownloadSample = async () => {
    if (!selectedSubCategory) {
      toast.warning('No category selected', 'Please select a subcategory first.');
      return;
    }
    try {
      await downloadFile(selectedSubCategory.SubCategoryID, 'sample');
      toast.success('Download started', 'Sample file is downloading.');
    } catch (error) {
      console.error('Failed to download sample:', error);
      toast.error('Download failed', 'Failed to download sample file.');
    }
  };

  const handleDownloadResults = () => {
    if (!validationResults || !validationResults.results) return;

    // Filter results based on current statusFilter
    const filtered = validationResults.results.filter(r =>
      statusFilter === 'all' || r.status.toLowerCase() === statusFilter
    );

    if (filtered.length === 0) {
      toast.warning('No data', 'No records match the current filter to download.');
      return;
    }

    const headers = validationResults.headers || [];

    // Build rows for Excel: original data columns + Status + Fields with Errors + Error Details
    const excelRows = filtered.map(r => {
      const row: Record<string, any> = {};
      row['Row Number'] = r.rowNumber;
      if (headers.length > 0) {
        headers.forEach(h => {
          row[h] = r.rowData?.[h] ?? '';
        });
      }
      row['Status'] = r.status;
      row['Fields with Errors'] = r.fieldsWithIssues?.join(', ') || '';
      row['Error Details'] = r.message || '';
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(excelRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Validation Results');

    const filterLabel = statusFilter === 'all' ? 'All' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1);
    const fileName = `${selectedDataCategory || 'Validation'}_${filterLabel}_Results.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success('Download started', `${filtered.length} ${filterLabel.toLowerCase()} record(s) downloading.`);
  };


  return (
    <div className="flex-1 flex flex-col bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 bg-blue-600 rounded-full" />
            <div>
              <h1 className="text-lg font-bold text-gray-900 leading-tight">
                {getCategoryTitle(category)}
              </h1>
              {selectedDataCategory && (
                <p className="text-xs text-blue-600 font-medium mt-0.5">{selectedDataCategory}</p>
              )}
            </div>
          </div>
          
          {/* Data Category Dropdown */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className={`text-sm font-medium ${showDropdownHighlight ? 'text-blue-600' : 'text-gray-700'}`}>
                Choose Data Category:
              </label>
              {loadingSubcategories ? (
                <div className="flex items-center gap-2 px-3 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm text-gray-500">Loading...</span>
                </div>
              ) : (
                <select
                  value={selectedDataCategory}
                  onChange={(e) => handleSubCategoryChange(e.target.value)}
                  className={`px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-48 ${
                    showDropdownHighlight ? 'dropdown-highlight' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select Category</option>
                  {subcategories
                    ?.filter((subcategory) => hasPermission(subcategory.SubCategoryName, 'can_read'))
                    .map((subcategory) => (
                    <option key={subcategory.SubCategoryID} value={subcategory.SubCategoryName}>
                      {subcategory.SubCategoryName}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button 
              className={`flex items-center gap-2 px-4 py-2 ${
                selectedDataCategory
                  ? 'bg-sap-primary hover:bg-sap-primary/90 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              onClick={selectedDataCategory ? handleUploadClick : undefined}
              disabled={!selectedDataCategory}
            >
              <Upload className="w-4 h-4" />
              Upload
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileInputChange}
              className="hidden"
            />
            <Button 
              variant="outline" 
              className={`flex items-center gap-2 px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                selectedDataCategory
                  ? 'border-gray-300 hover:bg-gray-50'
                  : 'border-gray-200 text-gray-400'
              }`}
              onClick={selectedDataCategory ? handleDownloadTemplate : undefined}
              disabled={!selectedDataCategory || !selectedSubCategory}
            >
              <Download className="w-4 h-4" />
              Template
            </Button>
            <Button 
              variant="outline" 
              className={`flex items-center gap-2 px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                selectedDataCategory
                  ? 'bg-sap-success/10 text-sap-success border-sap-success/20 hover:bg-sap-success/20'
                  : 'bg-gray-100 text-gray-400 border-gray-200'
              }`}
              onClick={selectedDataCategory ? handleDownloadSample : undefined}
              disabled={!selectedDataCategory || !selectedSubCategory}
            >
              <FileText className="w-4 h-4" />
              Sample
            </Button>
          </div>
        </div>
        
        {/* Horizontal Line */}
        <div className="mt-4 border-t border-gray-300"></div>


        {/* Single Line Layout: Tabs, File Info, and Validation Summary */}
        <div className="mt-3 flex items-center justify-between gap-4">
          {/* Left side: Tabs */}
          <div className="flex items-center gap-4">
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
              <button
                onClick={() => setActiveTab('upload')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-300 ${
                  activeTab === 'upload'
                    ? 'bg-blue-900 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-blue-800 hover:text-white'
                }`}
              >
                <span className="transition-all duration-300">{getTabName()}</span>
              </button>
              <button
                onClick={() => selectedDataCategory && setActiveTab('instructions')}
                disabled={!selectedDataCategory}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  !selectedDataCategory
                    ? 'text-gray-400 cursor-not-allowed opacity-50'
                    : activeTab === 'instructions'
                    ? 'bg-blue-900 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-blue-800 hover:text-white'
                }`}
              >
                Field Instructions
              </button>
            </div>
            
            {/* AI Helper message beside Field Instructions tab */}
            {activeTab === 'instructions' && (
              <div className="flex items-center gap-2 px-2 py-1.5 bg-blue-50 border border-blue-200 rounded-md">
                <span className="text-xs text-blue-800">
                  Selected <span className="font-semibold text-blue-900 bg-blue-100 px-1.5 py-0.5 rounded">{selectedDataCategory || 'Field Instructions'}</span> - Upload Excel for validation. AI ready.
                </span>
              </div>
            )}
          </div>

          {/* Center: File Upload Status */}
          {uploadedFile && (
            <div className="flex items-center gap-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg flex-1 max-w-md">
              <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-blue-900 truncate">{uploadedFile.name}</p>
                <p className="text-xs text-blue-600">{fileData.length} rows loaded</p>
              </div>
              <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300 flex-shrink-0">
                Ready for Validation
              </Badge>
            </div>
          )}
          
          {/* Right side: Action Buttons or Validation Summary */}
          {activeTab === 'instructions' ? (
            hasPermission('Field Instructions', 'can_create') ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAddForm(true)}
                  className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors bg-white text-gray-600 hover:bg-blue-800 hover:text-white border border-gray-300 hover:border-blue-800 flex items-center gap-2"
                >
                  <Plus className="w-3 h-3" />
                  Add Row
                </button>
                <button
                  onClick={() => setShowExcelUpload(true)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-2 ${
                    selectedDataCategory
                      ? 'bg-blue-600 text-white hover:bg-blue-700 border border-blue-600 hover:border-blue-700'
                      : 'bg-white text-gray-600 hover:bg-blue-800 hover:text-white border border-gray-300 hover:border-blue-800'
                  }`}
                >
                  <Upload className="w-3 h-3" />
                  Upload Excel
                </button>
              </div>
            ) : null
          ) : (
            /* Validation Summary stat cards */
            validationResults && !isValidating && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-lg border border-gray-200">
                  <BarChart3 className="w-3.5 h-3.5 text-gray-500" />
                  <span className="text-xs text-gray-500">Total</span>
                  <span className="text-xs font-bold text-gray-800">{validationResults.summary.total}</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                  <span className="text-xs text-green-600">Valid</span>
                  <span className="text-xs font-bold text-green-700">{validationResults.summary.valid}</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-50 rounded-lg border border-yellow-200">
                  <AlertTriangle className="w-3.5 h-3.5 text-yellow-600" />
                  <span className="text-xs text-yellow-600">Warn</span>
                  <span className="text-xs font-bold text-yellow-700">{validationResults.summary.warnings}</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 rounded-lg border border-red-200">
                  <XCircle className="w-3.5 h-3.5 text-red-600" />
                  <span className="text-xs text-red-600">Error</span>
                  <span className="text-xs font-bold text-red-700">{validationResults.summary.errors}</span>
                </div>
                <button
                  onClick={() => { setValidationResults(null); setUploadedFile(null); setFileData([]); }}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  title="Clear results"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )
          )}
        </div>
        </div>
      </div>

      {/* Content - flex column so it fits viewport */}
      <div className="flex-1 flex flex-col min-h-0 p-6 overflow-hidden">
        {activeTab === 'upload' && (
          <div className="flex flex-col flex-1 min-h-0 space-y-4">
            {/* Show loading animation while subcategories are loading */}
            {loadingSubcategories ? (
              <LoadingAnimation message="Fetching validation categories..." />
            ) : isValidating ? (
              /* Show validation loading state */
              <div className="flex flex-col items-center justify-center min-h-[400px]">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10 flex flex-col items-center gap-5 w-full max-w-sm">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full border-4 border-blue-100 flex items-center justify-center">
                      <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-base font-semibold text-gray-800">Validating data...</p>
                    <p className="text-sm text-gray-500 mt-1">Running AI validation rules</p>
                  </div>
                  {validationProgress.total > 1 && (
                    <div className="w-full">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>{validationProgress.percentage}% complete</span>
                        <span>{validationProgress.current} of {validationProgress.total} rows validated</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${validationProgress.percentage}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* AI Helper - Show in empty content area or when no validation results */
              (!uploadedFile || !validationResults) && (
                <div className="flex justify-center items-center min-h-[400px]">
                  <AIHelper 
                    selectedCategory={selectedDataCategory}
                    hasUploadedFile={!!uploadedFile}
                    className="fade-in-up w-full max-w-2xl"
                  />
                </div>
              )
            )}
            


            {/* Final Validation Results - fills remaining height, fits screen */}
            {validationResults && !isClearingOutput && !isValidating && (
              <div className={`flex flex-col flex-1 min-h-0 transition-opacity duration-300 ${isClearingOutput ? 'opacity-0' : 'opacity-100'}`}>
                <div className="flex items-center gap-3 bg-white px-3 py-2 rounded-lg border mb-2 flex-shrink-0">
                  <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-xs font-medium text-gray-500 flex-shrink-0">Filter:</span>
                  {(['all', 'valid', 'warning', 'error'] as const).map((val) => (
                    <button
                      key={val}
                      onClick={() => setStatusFilter(val)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        statusFilter === val
                          ? val === 'all' ? 'bg-gray-800 text-white'
                            : val === 'valid' ? 'bg-green-600 text-white'
                            : val === 'warning' ? 'bg-yellow-500 text-white'
                            : 'bg-red-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {val === 'all' ? 'All' : val === 'valid' ? 'Valid' : val === 'warning' ? 'Warnings' : 'Errors'}
                    </button>
                  ))}
                  <div className="ml-auto">
                    <button
                      onClick={handleDownloadResults}
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                      title={`Download ${statusFilter === 'all' ? 'all' : statusFilter} results as Excel`}
                    >
                      <FileDown className="w-3.5 h-3.5" />
                      Download
                    </button>
                  </div>
                </div>
                <div className="flex-1 min-h-0 flex flex-col">
                  <ValidationResults
                    results={validationResults}
                    statusFilter={statusFilter}
                    subcategoryName={selectedDataCategory}
                    checkedFields={checkedFieldsForValidation}
                    onCheckedFieldsLoaded={setCheckedFieldsForValidation}
                  />
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-500 mt-2 flex-shrink-0">
                  Showing {validationResults.results.length} records
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'instructions' && (
          <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
            <FieldInstructions
              category={category}
              subcategory={selectedDataCategory}
              refreshTrigger={refreshInstructions}
            />
            {selectedDataCategory && (
              <BusinessRules
                subcategory={selectedDataCategory}
                refreshTrigger={refreshInstructions}
              />
            )}
          </div>
        )}

      </div>

      {/* Add Field Instruction Form Modal */}
      <AddFieldInstructionForm
        category={category}
        subcategory={selectedDataCategory}
        isOpen={showAddForm}
        onClose={() => setShowAddForm(false)}
        onSuccess={() => {
          setRefreshInstructions(prev => prev + 1);
          setShowAddForm(false);
        }}
      />

      {/* Excel Upload Form Modal - when on Field Instructions tab, updates Field Instructions table; otherwise data table */}
      <ExcelUploadForm
        category={category}
        subcategory={selectedDataCategory}
        isOpen={showExcelUpload}
        onClose={() => setShowExcelUpload(false)}
        uploadMode={activeTab === 'instructions' ? 'fieldInstructions' : 'schema'}
        onSuccess={() => {
          console.log('MainContent: Excel upload successful, refreshing instructions...');
          setRefreshInstructions(prev => {
            const newValue = prev + 1;
            console.log('MainContent: Refresh trigger updated to:', newValue);
            return newValue;
          });
          setShowExcelUpload(false);
        }}
      />
    </div>
  );
};

export default MainContent;
